/**
 * Meta + Google Ads Attribution Capture
 *
 * Captures fbclid, _fbp cookie, Google Ads click ids (gclid/gbraid/wbraid,
 * plus the _gcl_aw cookie as a gclid fallback), and UTM parameters from the
 * landing URL. Stores in localStorage so they persist across page navigations
 * and are available at signup time.
 */

const STORAGE_KEY = 'riw_meta_attribution';

export interface MetaAttributionData {
  fbclid: string | null;
  fbp: string | null;
  fbc: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  landing_url: string | null;
  captured_at: number; // unix ms
}

/**
 * Read the _fbp cookie value set by Meta's fbevents.js pixel.
 * Format: fb.1.{timestamp}.{random}
 */
function getFbpCookie(): string | null {
  try {
    const match = document.cookie
      .split('; ')
      .find((c) => c.startsWith('_fbp='));
    return match ? match.split('=').slice(1).join('=') : null;
  } catch {
    return null;
  }
}

/**
 * Read the _fbc cookie value set by Meta's fbevents.js pixel when a visit
 * carries fbclid. Canonical source for fbc when we didn't see the fbclid
 * ourselves (e.g. the click landed on the marketing site first).
 */
function getFbcCookie(): string | null {
  try {
    const match = document.cookie
      .split('; ')
      .find((c) => c.startsWith('_fbc='));
    return match ? match.split('=').slice(1).join('=') : null;
  } catch {
    return null;
  }
}

/**
 * Construct the fbc (click ID) value from fbclid.
 * Format: fb.1.{timestamp_ms}.{fbclid}
 * See: https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc
 */
function buildFbc(fbclid: string): string {
  return `fb.1.${Date.now()}.${fbclid}`;
}

/**
 * Read the gclid from the `_gcl_aw` cookie the Google Ads tag (gtag) writes.
 * Format: GCL.{timestamp_s}.{gclid}. Used as a fallback source of gclid when
 * the `gclid` query param is not present on the current URL (e.g. the tag set
 * the cookie after a redirect, or on a later navigation within the session).
 */
function getGclidFromGclAwCookie(): string | null {
  try {
    const match = document.cookie
      .split('; ')
      .find((c) => c.startsWith('_gcl_aw='));
    if (!match) return null;
    const value = match.split('=').slice(1).join('=');
    const parts = value.split('.');
    // GCL.<ts>.<gclid> — gclid is everything after the second dot.
    return parts.length >= 3 ? parts.slice(2).join('.') : null;
  } catch {
    return null;
  }
}

/**
 * Capture attribution data from the current URL and cookies.
 * Should be called once on initial app load.
 * Only writes when the URL carries new attribution params, and merges into the
 * stored snapshot per network — so a later Google click updates gclid without
 * erasing Meta's fbclid/fbc, and vice versa.
 */
export function captureMetaAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const fbclid = params.get('fbclid');
    const utmSource = params.get('utm_source');
    const gclid = params.get('gclid');
    const gbraid = params.get('gbraid');
    const wbraid = params.get('wbraid');

    // Only capture if there's new attribution data in the URL
    const hasNewAttribution = fbclid || utmSource || gclid || gbraid || wbraid;

    // No new attribution params in the URL: backfill cookie-sourced ids
    // (fbp/fbc from the Meta pixel, gclid from the Google tag's _gcl_aw)
    // into the stored snapshot — creating one if nothing is stored yet.
    if (!hasNewAttribution) {
      backfillFromCookies();
      return;
    }

    // Merge into the previous snapshot instead of replacing it: a Google ad
    // click (gclid, usually with no UTMs) must not null out the stored Meta
    // ids and vice versa. Each network's fields are only overwritten by a new
    // touch carrying that network's own params; UTMs move as one group keyed
    // on utm_source.
    let prev: Partial<MetaAttributionData> = {};
    try {
      prev = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') || {};
    } catch {
      prev = {};
    }

    const data: MetaAttributionData = {
      fbclid: fbclid || prev.fbclid || null,
      fbp: getFbpCookie() || prev.fbp || null,
      fbc: fbclid
        ? buildFbc(fbclid)
        : prev.fbc || getFbcCookie() || null,
      gclid: gclid || getGclidFromGclAwCookie() || prev.gclid || null,
      gbraid: gbraid || prev.gbraid || null,
      wbraid: wbraid || prev.wbraid || null,
      utm_source: utmSource || prev.utm_source || null,
      utm_medium: utmSource
        ? params.get('utm_medium')
        : prev.utm_medium || null,
      utm_campaign: utmSource
        ? params.get('utm_campaign')
        : prev.utm_campaign || null,
      utm_content: utmSource
        ? params.get('utm_content')
        : prev.utm_content || null,
      utm_term: utmSource ? params.get('utm_term') : prev.utm_term || null,
      landing_url: window.location.href,
      captured_at: Date.now(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silent fail — attribution is non-critical
  }
}

/**
 * Backfill ids that only exist as cookies into the stored snapshot: _fbp/_fbc
 * from the Meta pixel and the gclid inside _gcl_aw from the Google tag. These
 * cookies live on the parent domain (.reinvestwealth.com), so they carry
 * attribution from ad clicks that landed on the marketing site even when the
 * app itself was reached on a clean URL — in that case nothing is stored yet
 * and a snapshot is created from the cookies alone.
 * Returns the up-to-date snapshot, or null when there is nothing to store.
 */
function backfillFromCookies(): MetaAttributionData | null {
  try {
    let data: MetaAttributionData | null = null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) data = JSON.parse(stored);

    const fbp = data?.fbp ? null : getFbpCookie();
    const fbc = data?.fbc ? null : getFbcCookie();
    const gclid = data?.gclid ? null : getGclidFromGclAwCookie();
    if (!fbp && !fbc && !gclid) return data;

    data = data ?? {
      fbclid: null,
      fbp: null,
      fbc: null,
      gclid: null,
      gbraid: null,
      wbraid: null,
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
      landing_url: window.location.href,
      captured_at: Date.now(),
    };
    if (fbp) data.fbp = fbp;
    if (fbc) data.fbc = fbc;
    if (gclid) data.gclid = gclid;

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    return data;
  } catch {
    return null;
  }
}

/**
 * Retrieve stored attribution data for sending to the backend at signup,
 * after a final cookie backfill (the pixel/tag may have written _fbp/_fbc/
 * _gcl_aw after the initial capture, or on another subdomain entirely).
 * Returns null if no attribution data has been captured.
 */
export function getMetaAttribution(): MetaAttributionData | null {
  return backfillFromCookies();
}

/**
 * Generate a unique event ID for deduplication between browser Pixel and CAPI.
 * Uses crypto.randomUUID when available, falls back to timestamp+random.
 */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}
